'use client'
import { faEdit, faTrash } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  Checkbox,
  FormControlLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material'
import { DesktopDatePicker as DatePicker, DesktopDateTimePicker } from '@mui/x-date-pickers'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { flexRender } from '@tanstack/react-table'
import { format } from 'date-fns'
import React, { useCallback, useMemo, useState } from 'react'
import Switch from 'src/components/era-package/era/Switch'
import Autocomplete from 'src/components/era-package/forms/AltAutocomplete'
import Button from 'src/components/era-package/forms/Button'

const toInputString = (value) => (value == null ? '' : (typeof value === 'string' ? value : String(value)))

export const useRowEdit = (
  fields, optionsMap, data,
  customColumn, generatedColumns, actions,
  disableEdit, editMode, handleEditRow,
  disableAdd, handleAddRow,
  setHasUnsaved, onDataChange,
  onRowClick,
) => {
  const rowEditEnabled = editMode === 'row' && !disableEdit
  const isActionEdit = rowEditEnabled && !!onRowClick

  const [rowEditingIndex, setRowEditingIndex] = useState(null)
  const [rowDraft, setRowDraft] = useState({})
  const [rowErrors, setRowErrors] = useState({})
  const [isNewRowDraft, setIsNewRowDraft] = useState(false)
  const [lockedData, setLockedData] = useState(null)

  const tableData = rowEditEnabled && rowEditingIndex !== null && Array.isArray(lockedData)
    ? lockedData
    : data

  const fieldMap = useMemo(() => fields.reduce((acc, field) => ({ ...acc, [field.name]: field }), {}), [fields])
  const isInteractionLocked = rowEditEnabled && rowEditingIndex !== null

  const beginRowEdit = useCallback((row, index, sourceData = data, isNew = false) => {
    if (!rowEditEnabled || disableEdit) return
    if (rowEditingIndex !== null && rowEditingIndex !== index) return

    setRowEditingIndex(index)
    setRowDraft({ ...row })
    setRowErrors({})
    setIsNewRowDraft(isNew)
    setLockedData(Array.isArray(sourceData) ? [...sourceData] : [])
  }, [data, disableEdit, rowEditEnabled, rowEditingIndex])

  const handleRowDraftChange = useCallback((fieldName, value, clearFields) => {
    setRowDraft((prev) => {
      const next = { ...prev, [fieldName]: value }
      if (Array.isArray(clearFields)) {
        clearFields.forEach((f) => { next[f] = '' })
      }
      return next
    })
    setRowErrors((prev) => ({ ...prev, [fieldName]: '' }))
  }, [])

  const validateRowDraft = useCallback(() => {
    const nextErrors = {}

    fields.forEach((field) => {
      if (!field.required) return

      const value = rowDraft?.[field.name]
      const isEmptyArray = Array.isArray(value) && value.length === 0
      const isEmptyString = typeof value === 'string' && value.trim() === ''
      const isEmptyValue = value === null || value === undefined || isEmptyString || isEmptyArray

      if (isEmptyValue) {
        nextErrors[field.name] = `${field.label} is required`
      }
    })

    setRowErrors(nextErrors)
    return Object.keys(nextErrors).length === 0
  }, [fields, rowDraft])

  const clearRowEditState = useCallback(() => {
    setRowEditingIndex(null)
    setRowDraft({})
    setRowErrors({})
    setIsNewRowDraft(false)
    setLockedData(null)
  }, [])

  const handleDeleteRowEdit = useCallback(() => {
    if (rowEditingIndex === null) return

    const sourceData = Array.isArray(lockedData) ? lockedData : data
    const nextData = sourceData.filter((_, idx) => idx !== rowEditingIndex)

    onDataChange(nextData)
    if (setHasUnsaved) {
      setHasUnsaved(true)
    }

    clearRowEditState()
  }, [clearRowEditState, data, lockedData, onDataChange, rowEditingIndex, setHasUnsaved])

  const handleSaveRowEdit = useCallback(() => {
    if (rowEditingIndex === null) return
    if (!validateRowDraft()) return

    const sourceData = Array.isArray(lockedData) ? lockedData : data
    const nextData = [...sourceData]
    nextData[rowEditingIndex] = rowDraft

    onDataChange(nextData)
    if (setHasUnsaved) {
      setHasUnsaved(true)
    }

    clearRowEditState()
  }, [clearRowEditState, data, lockedData, onDataChange, rowDraft, rowEditingIndex, setHasUnsaved, validateRowDraft])

  const handleCancelRowEdit = useCallback(() => {
    if (rowEditingIndex === null) return

    if (isNewRowDraft) {
      const sourceData = Array.isArray(lockedData) ? lockedData : data
      const nextData = sourceData.filter((_, idx) => idx !== rowEditingIndex)
      onDataChange(nextData)
    }

    clearRowEditState()
  }, [clearRowEditState, data, isNewRowDraft, lockedData, onDataChange, rowEditingIndex])

  const handleAddClick = useCallback(() => {
    if (!rowEditEnabled) {
      handleAddRow()
      return
    }

    if (disableEdit || disableAdd || rowEditingIndex !== null) return

    const newRow = fields.reduce((acc, field) => {
      if (field.defaultValue !== undefined) {
        acc[field.name] = field.defaultValue
        return acc
      }

      const type = field.type?.trim().toLowerCase()
      if (type === 'toggle' || type === 'switch' || type === 'checkbox') {
        acc[field.name] = false
      } else {
        acc[field.name] = ''
      }
      return acc
    }, {})

    const nextData = [...data, newRow]
    onDataChange(nextData)
    if (setHasUnsaved) {
      setHasUnsaved(true)
    }
    beginRowEdit(newRow, nextData.length - 1, nextData, true)
  }, [beginRowEdit, data, disableAdd, disableEdit, fields, handleAddRow, rowEditEnabled, onDataChange, rowEditingIndex, setHasUnsaved])

  const rowActionColumn = useMemo(() => {
    if (!actions?.length && !isActionEdit && !rowEditEnabled) return []

    return [{
      id: '__row_actions__',
      header: 'Actions',
      enableSorting: false,
      cell: ({ row }) => {
        const isEditing = row.index === rowEditingIndex

        if (!isEditing) {
          return (
            <Stack spacing={1} direction='row'
                   justifyContent='space-around' alignItems='center'
            >
              {actions?.length > 0 && actions.map((action, idx) => (
                <IconButton
                  size='small'
                  key={idx}
                  {...action.iconButtonProps}
                  onClick={(e) => action.onClick(e, row)}
                >
                  {action.icon}
                </IconButton>
              ))}
              {isActionEdit && (
                <IconButton
                  size='small'
                  onClick={() => beginRowEdit(row.original, row.index, tableData)}
                  title="Edit row"
                >
                  <FontAwesomeIcon icon={faEdit} />
                </IconButton>
              )}
            </Stack>
          )
        }

        return (
          <Stack spacing={1} onClick={(event) => event.stopPropagation()}>
            <Button size="small" minWidth={70} onClick={() => handleSaveRowEdit()}>
              Save
            </Button>
            <Button size="small" minWidth={70} variant="outlined" onClick={() => handleCancelRowEdit()}>
              Cancel
            </Button>
            {!isNewRowDraft && (
              <Button
                size="small"
                minWidth={70}
                variant="outlined"
                color="error"
                onClick={() => handleDeleteRowEdit()}
              >
                Delete
              </Button>
            )}
          </Stack>
        )
      },
    }]
  }, [handleCancelRowEdit, handleSaveRowEdit, handleDeleteRowEdit, rowEditEnabled, rowEditingIndex, actions, onRowClick, beginRowEdit, tableData, isNewRowDraft])

  const tableColumns = useMemo(() => customColumn
      ? [...generatedColumns, ...customColumn, ...rowActionColumn]
      : [...generatedColumns, ...rowActionColumn],
    [customColumn, generatedColumns, rowActionColumn])

  const handleRowClick = (row, index) => {
    if (!!onRowClick)
      onRowClick(row.original, index)

    if (disableEdit) return

    if (rowEditEnabled) {
      if (rowEditingIndex !== null || isActionEdit) return
      beginRowEdit(row.original, index, tableData)
    } else handleEditRow(row.original, index)
  }

  const renderEditableCell = useCallback((cell, rowIndex) => {
    if (!rowEditEnabled || rowEditingIndex !== rowIndex) {
      return flexRender(cell.column.columnDef.cell, cell.getContext())
    }

    const field = fieldMap[cell.column.id]

    if (!field)
      return flexRender(cell.column.columnDef.cell, cell.getContext())

    const value = rowDraft?.[field.name]
    const error = rowErrors[field.name]
    const type = field.type?.trim().toLowerCase() || 'text'
    const isRequired = typeof field.requiredCondition === 'function'
      ? Boolean(field.requiredCondition(rowDraft, rowDraft))
      : Boolean(field.required)
    const isDisabled = Boolean(field.disabled)
    const autoOptions = typeof optionsMap?.[field.name] === 'function'
      ? optionsMap[field.name](rowDraft)
      : Array.isArray(optionsMap?.[field.name]) ? optionsMap[field.name] : []
    const isAutoType = type.startsWith('auto')
    const toggleLabels = (Array.isArray(field.toggleLabels) && field.toggleLabels.length === 2)
      ? field.toggleLabels
      : ['Yes', 'No']

    const stopRowClick = (event) => event.stopPropagation()
    if (type === 'checkbox') {
      return (
        <FormControlLabel
          onClick={stopRowClick}
          control={(
            <Checkbox
              checked={Boolean(value)}
              disabled={isDisabled}
              onChange={(event) => handleRowDraftChange(field.name, event.target.checked)}
            />
          )}
          label=""
          sx={{ m: 0 }}
        />
      )
    }

    if (type === 'toggle') {
      return (
        <ToggleButtonGroup
          exclusive
          fullWidth
          color="primary"
          value={Boolean(value)}
          disabled={isDisabled}
          onClick={stopRowClick}
          onChange={(event, selected) => {
            if (selected === null || selected === undefined) return
            handleRowDraftChange(field.name, Boolean(selected))
          }}
          size="small"
        >
          <ToggleButton value={true} sx={{ width: '50%' }}>
            {toggleLabels[0]}
          </ToggleButton>
          <ToggleButton value={false} sx={{ width: '50%' }}>
            {toggleLabels[1]}
          </ToggleButton>
        </ToggleButtonGroup>
      )
    }

    if (type === 'switch') {
      return (
        <Stack onClick={stopRowClick}>
          <Switch
            checked={Boolean(value)}
            disabled={isDisabled}
            onChange={(event, checked) => handleRowDraftChange(field.name, Boolean(checked))}
            size="small"
            error={!!error}
          />
        </Stack>
      )
    }

    if (type === 'select') {
      return (
        <TextField
          select
          size="small"
          fullWidth
          value={toInputString(value)}
          required={isRequired}
          disabled={isDisabled}
          onClick={stopRowClick}
          onChange={(event) => handleRowDraftChange(field.name, event.target.value)}
          error={!!error}
          helperText={error}
          InputProps={
            field.icon && {
              startAdornment: (
                <InputAdornment position="start">{field.icon}</InputAdornment>
              ),
            }
          }
        >
          {autoOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.value} - {option.description}
            </MenuItem>
          ))}
        </TextField>
      )
    }

    if (isAutoType) {
      const filteredOptions = autoOptions.filter((option) => {
        if (!option || !option.parent) return true
        return rowDraft?.[option.parent] === option.code
      }).map((option) => ({
        value: option.value,
        description:
          type === 'auto value'
            ? option.value
            : type === 'auto description'
              ? option.description
              : `${option.value} - ${option.description}`,
      }))

      return (
        <Autocomplete
          size="small"
          fullWidth
          value={field.multiple
            ? (filteredOptions?.filter((option) => value?.includes(option.value)) || [])
            : filteredOptions?.find((option) => option.value === value) || (field.creatable ? value || '' : '')}
          required={isRequired}
          disabled={isDisabled}
          onClick={stopRowClick}
          onChange={(event, selectedValue) => {
            const normalizeOptionValue = (option) => {
              if (typeof option === 'string') return option
              return option?.value || ''
            }

            const normalizedValue = Array.isArray(selectedValue)
              ? selectedValue.map((option) => normalizeOptionValue(option)).filter(Boolean)
              : normalizeOptionValue(selectedValue)

            handleRowDraftChange(field.name, normalizedValue, field.clearFields)
          }}
          error={!!error}
          helperText={error}
          options={filteredOptions || []}
          getOptionLabel={(option) => {
            if (typeof option === 'string') return option
            return option.description || option.inputValue || ''
          }}
          isOptionEqualToValue={(option, selected) => {
            if (typeof selected === 'string') return option?.value === selected
            return option?.value === selected?.value
          }}
          multiple={field.multiple || false}
          disableCloseOnSelect={field.multiple || false}
          creatable={field.creatable}
        />
      )
    }

    if (type === 'date') {
      return (
        <Stack onClick={stopRowClick}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              value={value ? new Date(value) : null}
              disabled={isDisabled}
              inputFormat="MM-dd-yyyy"
              onChange={(date) => {
                const nextValue = date ? format(date, 'yyyy-MM-dd') : ''
                handleRowDraftChange(field.name, nextValue)
              }}
              renderInput={({ error: pickerError, ...params }) => (
                <TextField
                  size="small"
                  fullWidth
                  required={isRequired}
                  error={pickerError || !!error}
                  helperText={error}
                  {...params}
                />
              )}
            />
          </LocalizationProvider>
        </Stack>
      )
    }

    if (type === 'datetime') {
      return (
        <Stack onClick={stopRowClick}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DesktopDateTimePicker
              ampm={false}
              value={value ? new Date(value) : null}
              disabled={isDisabled}
              inputFormat="MM-dd-yyyy HH:mm"
              onChange={(date) => {
                const nextValue = date ? format(date, 'yyyy-MM-dd HH:mm') : ''
                handleRowDraftChange(field.name, nextValue)
              }}
              renderInput={({ error: pickerError, ...params }) => (
                <TextField
                  size="small"
                  fullWidth
                  required={isRequired}
                  error={pickerError || !!error}
                  helperText={error}
                  {...params}
                />
              )}
            />
          </LocalizationProvider>
        </Stack>
      )
    }

    if (type === 'textarea') {
      return (
        <TextField
          multiline
          minRows={3}
          size="small"
          fullWidth
          value={toInputString(value)}
          required={isRequired}
          disabled={isDisabled}
          onClick={stopRowClick}
          onChange={(event) => handleRowDraftChange(field.name, event.target.value)}
          error={!!error}
          helperText={error}
        />
      )
    }

    return (
      <TextField
        size="small"
        fullWidth
        type={type === 'number' ? 'number' : 'text'}
        value={toInputString(value)}
        required={isRequired}
        disabled={isDisabled}
        onClick={stopRowClick}
        onChange={(event) => handleRowDraftChange(field.name, event.target.value)}
        error={!!error}
        helperText={error}
        InputProps={
          field.icon && {
            startAdornment: (
              <InputAdornment position="start">{field.icon}</InputAdornment>
            ),
          }
        }
      />
    )
  }, [fieldMap, handleRowDraftChange, rowEditEnabled, optionsMap, rowDraft, rowEditingIndex, rowErrors])

  return {
    tableData, tableColumns,
    isInteractionLocked,
    isRowEditMode: rowEditEnabled, rowEditingIndex, beginRowEdit,
    handleAddClick, handleRowClick,
    renderEditableCell
  }
}